// The Evaluation rules tab (Lane 1344, UI redesign slice 2, plan 5.6): the rules
// every scenario starts from. The server writes each rule's sentence. Clicking
// "Confirmed" turns it, in place, into Edit and Delete (Ryan round 5: no pop-up);
// Edit opens the chat on that rule; Delete keeps the rule as deleted (History shows
// it). The two lines that describe the calculation itself always apply. A rule can
// still be made by hand behind "Add or change by hand".
import React, { useCallback, useEffect, useRef, useState } from 'react';
import RulesTab from './RulesTab';

function partsText(parts, total) {
  if (parts === null || parts === undefined) return 'The award';
  if (parts === 'all') return `All ${total.toLocaleString()}`;
  return parts.toLocaleString();
}

export default function EvaluationRulesTab({ api, eventId, summary, options, refreshKey, onAsk, focusRuleId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [openRow, setOpenRow] = useState(null);     // rule_id showing Edit / Delete
  const [byHand, setByHand] = useState(false);
  const tableRef = useRef(null);

  const load = useCallback(() => api.evaluationRules(eventId)
    .then((d) => { setData(d); setError(''); }).catch((e) => setError(e.message)), [api, eventId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  // A click anywhere else puts the status back (plan 5.6).
  useEffect(() => {
    if (!openRow) return undefined;
    const away = (e) => { if (tableRef.current && !e.target.closest('.srce-rule-actions')) setOpenRow(null); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [openRow]);

  const act = async (action, rule) => {
    setBusy(true);
    setError('');
    try {
      await api.ruleAction(eventId, action, { rule_id: rule.rule_id, expected_version: rule.version });
      setOpenRow(null);
      await load();
    } catch (e) {
      if (e.code === 'stale_rule') await load();
      setError(e.message);
    } finally { setBusy(false); }
  };

  if (byHand) {
    return (
      <section>
        <button type="button" className="ll-clr" onClick={() => { setByHand(false); load(); }}>Back to evaluation rules</button>
        <RulesTab api={api} eventId={eventId} summary={summary} options={options} refreshKey={refreshKey} />
      </section>
    );
  }
  if (error && !data) return <div className="srce-error" role="alert">{error}</div>;
  if (!data) return <div className="ll-loading">Loading…</div>;
  const final = ['awarded', 'cancelled'].includes(summary.status);
  return (
    <section className="srce-card2" ref={tableRef} aria-label="Evaluation rules">
      <div className="srce-card2-h">
        <span className="srce-card2-t">Evaluation rules</span>
        {!final && (
          <span className="srce-push srce-row">
            <button type="button" className="ll-clr" onClick={() => setByHand(true)}>Add or change by hand</button>
            {onAsk && <button type="button" className="srce-btn srce-btn--primary"
              onClick={() => onAsk('Add an evaluation rule: ')}>Add a rule with Wingman</button>}
          </span>
        )}
      </div>
      <div className="srce-mut srce-card2-f">Every scenario starts from these rules. A scenario can leave a rule out;
        Wingman's recommendation uses them all.</div>
      {error && <div className="srce-error" role="alert">{error}</div>}
      <div className="srce-rules">
        <div className="srce-rules-row srce-rules-head" role="row">
          <span>#</span><span>Evaluation rule</span><span className="srce-num">Parts</span><span>Set by</span><span>Status</span>
        </div>
        {data.fixed.map((f, i) => (
          <div key={`f${i}`} className="srce-rules-row" role="row">
            <span className="srce-mut">-</span><span title={f.sentence}>{f.sentence}</span><span className="srce-num">All</span>
            <span>Wingman</span><span className="srce-mut">{f.status_text}</span>
          </div>
        ))}
        {data.rules.map((r) => (
          <div key={r.rule_id} role="row"
            className={`srce-rules-row${focusRuleId && focusRuleId === r.rule_id ? ' srce-rules-row--new' : ''}`}>
            <span>{r.number}</span>
            <span title={r.sentence}>{r.sentence}</span>
            <span className="srce-num">{partsText(r.parts, data.parts_total)}</span>
            <span>{r.set_by}</span>
            <span className="srce-rule-actions">
              {final ? <span>{r.status_text}</span> : openRow === r.rule_id ? (
                <span className="srce-row">
                  {r.state === 'proposed' && (
                    <button type="button" className="srce-btn srce-btn--sm" disabled={busy} onClick={() => act('confirm', r)}>Confirm</button>
                  )}
                  {onAsk && (
                    <button type="button" className="srce-btn srce-btn--sm" disabled={busy}
                      onClick={() => { setOpenRow(null); onAsk(`Change rule ${r.number} ("${r.sentence}"): `); }}>Edit</button>
                  )}
                  <button type="button" className="srce-btn srce-btn--sm srce-btn--neg" disabled={busy} onClick={() => act('reject', r)}>Delete</button>
                </span>
              ) : (
                <button type="button" className={`srce-status-btn${r.state === 'proposed' ? ' srce-warn' : ''}`}
                  onClick={() => setOpenRow(r.rule_id)}>{r.status_text}</button>
              )}
            </span>
          </div>
        ))}
      </div>
      {data.deleted.length > 0 && (
        <details className="srce-card2-f">
          <summary className="srce-mut">Deleted rules ({data.deleted.length})</summary>
          <ul className="srce-list">{data.deleted.map((r) => (
            <li key={r.rule_id} className="srce-row">
              <span className="srce-mut srce-ell" title={r.sentence}>{r.sentence}</span>
              {!final && <button type="button" className="ll-clr srce-push" disabled={busy}
                onClick={() => act('confirm', r)}>Restore</button>}
            </li>
          ))}</ul>
        </details>
      )}
    </section>
  );
}
